import { Action, AnyAction, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { DEFAULT_THEME_NAME, IThemeOption } from "../../constants/constants";
import { ErrorMessageAction } from "../../constants/types";
import { uploadLookupFileSuccess, uploadTemplateSuccess } from "../actions/NewTopologyActions";

function isErrorMessageAction(action: AnyAction): action is ErrorMessageAction {
  return action.type.endsWith("_ERROR") &&
    action.error !== undefined &&
    action.type !== "GET_USER_REGION_ERROR";
}

function isSuccessAction(action: Action<string>): action is Action {
  return action.type.endsWith("_SUCCESS");
}

function isFailureAction(action: Action<string>): action is Action {
  const type = action.type;
  return type.endsWith("_ERROR");
}

function isRequestAction(action: Action<string>): action is Action {
  return action.type.endsWith("_REQUESTED");
}

export type IUser = {
  upn: string;
  name: string;
};

export type IAppState = {
  user: IUser;
  theme: IThemeOption;
  showNavigationPanel: boolean;
  fetching: boolean;
  error: string;
  apiCallsInProgress: number;
};

export const initialAppState: IAppState = {
  user: {
    upn: "",
    name: "",
  },
  theme: DEFAULT_THEME_NAME,
  showNavigationPanel: false,
  fetching: false,
  error: "",
  apiCallsInProgress: 0,
};

const appSlice = createSlice({
  name: "appState",
  initialState: initialAppState,
  reducers: {
    setUser(state, action: PayloadAction<IUser>) {
      state.user = action.payload;
    },
    setTheme(state, action: PayloadAction<IThemeOption>) {
      state.theme = action.payload;
    },
    showNavigationPanel(state) {
      state.showNavigationPanel = true;
    },
    hideNavigationPanel(state) {
      state.showNavigationPanel = false;
    },
    clearError(state) {
      state.error = "";
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadLookupFileSuccess, (state) => {
        state.apiCallsInProgress--;
        state.fetching = state.apiCallsInProgress > 0;
      })
      .addCase(uploadTemplateSuccess, (state) => {
        state.apiCallsInProgress--;
        state.fetching = state.apiCallsInProgress > 0;
      })
      .addMatcher(isRequestAction, (state) => {
        state.apiCallsInProgress++;
        state.fetching = state.apiCallsInProgress > 0;
      })
      .addMatcher(isSuccessAction, (state) => {
        state.apiCallsInProgress--;
        state.fetching = state.apiCallsInProgress > 0;
      })
      .addMatcher(isFailureAction, (state) => {
        state.apiCallsInProgress--;
        state.fetching = state.apiCallsInProgress > 0;
      })
      .addMatcher(isErrorMessageAction, (state, action) => {
        state.error = action.error;
      });
  },
});

export const { setTheme, showNavigationPanel, hideNavigationPanel, setUser, clearError, setError } = appSlice.actions;

export default appSlice.reducer;
